import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Save,
  Plus,
  Trash2,
  X,
  Circle,
  Tag,
  CalendarDays,
} from 'lucide-react';

const BACKEND_URL = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  if (!url) return '';
  return url.startsWith('http') ? url : 'https://' + url;
})();

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const INPUT_STYLE = {
  background: 'rgba(255,255,255,0.025)',
  border: '1px solid var(--b1)',
  color: 'var(--white)',
};

const CURRENCIES = [
  { value: 'GBP', label: 'GBP · £' },
  { value: 'USD', label: 'USD · $' },
  { value: 'EUR', label: 'EUR · €' },
];

const CURRENCY_SYMBOL = { GBP: '£', USD: '$', EUR: '€' };

// ─── Reusable toggle ───────────────────────────────────────────────
function Toggle({ checked, onChange, testId, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className="relative w-11 h-6 rounded-full transition-colors flex-shrink-0"
      style={{ background: checked ? 'var(--a)' : 'var(--b2)', opacity: disabled ? 0.5 : 1 }}
    >
      <span
        className="absolute top-0.5 w-5 h-5 rounded-full transition-transform"
        style={{
          left: 2,
          background: 'var(--white)',
          transform: checked ? 'translateX(20px)' : 'translateX(0)',
        }}
      />
    </button>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId }) {
  return (
    <div className="flex items-center justify-between py-2.5" style={{ borderBottom: '1px solid var(--b1)' }}>
      <div className="pr-3">
        <p className="font-body text-sm" style={{ color: 'var(--white)' }}>{label}</p>
        {hint && <p className="font-body text-xs mt-0.5" style={{ color: 'var(--ts)' }}>{hint}</p>}
      </div>
      <Toggle checked={checked} onChange={onChange} testId={testId} />
    </div>
  );
}

// ─── Status badge for promo codes ──────────────────────────────────
function PromoStatusBadge({ status }) {
  const isActive = status === 'active';
  return (
    <span
      className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded"
      style={{
        background: isActive ? 'var(--ok-10)' : 'var(--danger-10)',
        color: isActive ? 'var(--ok)' : 'var(--danger)',
      }}
    >
      <Circle size={8} fill={isActive ? 'var(--ok)' : 'var(--danger)'} style={{ color: isActive ? 'var(--ok)' : 'var(--danger)' }} />
      {isActive ? 'Active' : 'Expired'}
    </span>
  );
}

// ─── Promo modal ───────────────────────────────────────────────────
function PromoModal({ onCancel, onSave, saving }) {
  const [form, setForm] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    max_uses: '',
    expires_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  });
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onEsc = (e) => e.key === 'Escape' && onCancel();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onCancel]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    try {
      await onSave({
        ...form,
        max_uses: form.max_uses === '' ? null : Number(form.max_uses),
        discount_value: Number(form.discount_value),
      });
    } catch (ex) {
      setErr(ex.message || 'Save failed');
    }
  };

  return (
    <div
      data-testid="admin-promo-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(5,5,10,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={onCancel}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg p-6"
        style={CARD_STYLE}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
              new · promo code
            </p>
            <h2 className="font-display text-xl font-bold" style={{ color: 'var(--white)' }}>Add Promo Code</h2>
          </div>
          <button type="button" onClick={onCancel} className="p-1" style={{ color: 'var(--ts)' }} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {err && (
          <div
            data-testid="admin-promo-modal-error"
            className="p-2 rounded mb-3"
            style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}
          >
            <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{err}</p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Code</label>
            <Input
              data-testid="admin-promo-modal-code"
              value={form.code}
              onChange={(e) => set('code', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 20))}
              placeholder="LAUNCH20"
              className="font-mono text-sm h-10 rounded-[3px] uppercase tracking-[0.15em]"
              style={INPUT_STYLE}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Discount Type</label>
              <select
                data-testid="admin-promo-modal-type"
                value={form.discount_type}
                onChange={(e) => set('discount_type', e.target.value)}
                className="w-full font-body text-sm h-10 rounded-[3px] px-2"
                style={INPUT_STYLE}
              >
                <option value="percentage" style={{ background: 'var(--l1)' }}>Percentage</option>
                <option value="fixed" style={{ background: 'var(--l1)' }}>Fixed £</option>
              </select>
            </div>
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>
                Value {form.discount_type === 'percentage' ? '(%)' : '(£)'}
              </label>
              <Input
                data-testid="admin-promo-modal-value"
                type="number"
                min={1}
                max={form.discount_type === 'percentage' ? 99 : 10000}
                value={form.discount_value}
                onChange={(e) => set('discount_value', e.target.value)}
                className="font-mono text-sm h-10 rounded-[3px]"
                style={INPUT_STYLE}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Max Uses (blank = unlimited)</label>
              <Input
                data-testid="admin-promo-modal-max-uses"
                type="number"
                min={1}
                value={form.max_uses}
                onChange={(e) => set('max_uses', e.target.value)}
                placeholder="unlimited"
                className="font-mono text-sm h-10 rounded-[3px]"
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Expires</label>
              <Input
                data-testid="admin-promo-modal-expires"
                type="date"
                value={form.expires_at}
                onChange={(e) => set('expires_at', e.target.value)}
                className="font-mono text-sm h-10 rounded-[3px]"
                style={INPUT_STYLE}
                required
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="font-body text-xs px-3 py-2 rounded-[3px]"
            style={{ color: 'var(--tm)', border: '1px solid var(--b1)' }}
          >
            Cancel
          </button>
          <Button
            type="submit"
            data-testid="admin-promo-modal-save"
            disabled={saving}
            className="btn-shimmer font-body text-xs px-4 py-2 rounded-[3px]"
            style={{ background: 'var(--a)', color: 'var(--white)', opacity: saving ? 0.6 : 1 }}
          >
            {saving ? 'Creating…' : 'Create Code'}
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Main section ──────────────────────────────────────────────────
export default function PricingControlSection() {
  const { adminFetch } = useAdminAuth();
  const [courses, setCourses] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [courseDraft, setCourseDraft] = useState(null);
  const [courseOriginal, setCourseOriginal] = useState(null);
  const [rulesDraft, setRulesDraft] = useState(null);
  const [rulesOriginal, setRulesOriginal] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [promoModalOpen, setPromoModalOpen] = useState(false);
  const [creatingPromo, setCreatingPromo] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Service Pricing (Section 5) — per-row dirty tracking + per-row save
  const [servicePricingOriginal, setServicePricingOriginal] = useState({});
  const [servicePricingDrafts, setServicePricingDrafts] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);
  const servicePricingMeta = useRef([]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cRes, rRes, pRes, spRes] = await Promise.all([
        adminFetch(`${BACKEND_URL}/api/admin/courses`),
        adminFetch(`${BACKEND_URL}/api/admin/pricing/rules`),
        adminFetch(`${BACKEND_URL}/api/admin/promo-codes`),
        adminFetch(`${BACKEND_URL}/api/admin/service-pricing`),
      ]);
      if (!cRes.ok)  throw new Error(`courses: ${cRes.status}`);
      if (!rRes.ok)  throw new Error(`rules: ${rRes.status}`);
      if (!pRes.ok)  throw new Error(`promo-codes: ${pRes.status}`);
      if (!spRes.ok) throw new Error(`service-pricing: ${spRes.status}`);
      const cBody  = await cRes.json();
      const rBody  = await rRes.json();
      const pBody  = await pRes.json();
      const spBody = await spRes.json();
      setCourses(cBody.courses);
      setRulesDraft(rBody.rules);
      setRulesOriginal(rBody.rules);
      setPromoCodes(pBody.promo_codes);
      // Service pricing — drafts = original on load
      const byId = {};
      for (const row of spBody.rows) {
        byId[row.id] = { price: row.price ?? '', per_month: !!row.per_month, notes: row.notes || '' };
      }
      setServicePricingOriginal(byId);
      setServicePricingDrafts(JSON.parse(JSON.stringify(byId)));
      // Keep full-label map for render
      servicePricingMeta.current = spBody.rows;
      // Default selected: Cybersecurity Foundations (or first)
      const def =
        cBody.courses.find((c) => c.name === 'Cybersecurity Foundations') ||
        cBody.courses[0];
      if (def) {
        setSelectedId(def.id);
        setCourseDraft(pickCourseDraft(def));
        setCourseOriginal(pickCourseDraft(def));
      }
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [adminFetch]);

  useEffect(() => { loadAll(); }, [loadAll]);

  function pickCourseDraft(c) {
    return {
      prices: { ...c.prices },
      corporate: c.corporate || 'Contact for Quote',
      enable_instalments: !!c.enable_instalments,
      founding_intake_badge: !!c.founding_intake_badge,
      show_founding_countdown: !!c.show_founding_countdown,
      founding_intake_deadline: c.founding_intake_deadline || '',
    };
  }

  const courseDirty = useMemo(
    () => JSON.stringify(courseDraft) !== JSON.stringify(courseOriginal),
    [courseDraft, courseOriginal],
  );
  const rulesDirty = useMemo(
    () => JSON.stringify(rulesDraft) !== JSON.stringify(rulesOriginal),
    [rulesDraft, rulesOriginal],
  );
  const isDirty = courseDirty || rulesDirty;

  const handleSelect = (id) => {
    if (courseDirty) {
      const ok = window.confirm('You have unsaved pricing changes for this course. Discard and switch?');
      if (!ok) return;
    }
    const c = courses.find((x) => x.id === id);
    if (!c) return;
    setSelectedId(id);
    setCourseDraft(pickCourseDraft(c));
    setCourseOriginal(pickCourseDraft(c));
  };

  const handleSaveAll = async () => {
    if (!isDirty) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (courseDirty && selectedId) {
        const res = await adminFetch(`${BACKEND_URL}/api/admin/courses/${selectedId}/pricing`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...courseDraft,
            founding_intake_deadline: courseDraft.founding_intake_deadline || null,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Course pricing save failed: ${res.status}`);
        }
        const { course } = await res.json();
        setCourses((prev) => prev.map((c) => (c.id === course.id ? course : c)));
        setCourseDraft(pickCourseDraft(course));
        setCourseOriginal(pickCourseDraft(course));
      }
      if (rulesDirty) {
        const res = await adminFetch(`${BACKEND_URL}/api/admin/pricing/rules`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...rulesDraft,
            founding_intake_deadline: rulesDraft.founding_intake_deadline || null,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Rules save failed: ${res.status}`);
        }
        const { rules } = await res.json();
        setRulesDraft(rules);
        setRulesOriginal(rules);
      }
      setSuccess('Changes saved.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePromo = async (payload) => {
    setCreatingPromo(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/promo-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Create failed: ${res.status}`);
      }
      const { promo_code } = await res.json();
      setPromoCodes((prev) => [...prev, promo_code]);
      setPromoModalOpen(false);
    } finally {
      setCreatingPromo(false);
    }
  };

  const handleDeletePromo = async (p) => {
    if (!window.confirm(`Delete promo code "${p.code}"?`)) return;
    setDeletingId(p.id);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/promo-codes/${p.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed: ${res.status}`);
      setPromoCodes((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const updateServicePricingDraft = (id, patch) => {
    setServicePricingDrafts((prev) => ({
      ...prev,
      [id]: { ...prev[id], ...patch },
    }));
  };

  const handleSaveServicePricingRow = async (id) => {
    const draft = servicePricingDrafts[id];
    if (!draft) return;
    setSavingRowId(id);
    setError(null);
    try {
      const payload = {
        price: draft.price === '' ? null : Number(draft.price),
        per_month: !!draft.per_month,
        notes: draft.notes || '',
      };
      const res = await adminFetch(`${BACKEND_URL}/api/admin/service-pricing/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Save failed: ${res.status}`);
      }
      const { row } = await res.json();
      // Reset original for this row to the freshly-saved values
      const fresh = { price: row.price ?? '', per_month: !!row.per_month, notes: row.notes || '' };
      setServicePricingOriginal((prev) => ({ ...prev, [id]: fresh }));
      setServicePricingDrafts((prev) => ({ ...prev, [id]: { ...fresh } }));
      setSuccess(`Saved · ${row.full_label || id}`);
      setTimeout(() => setSuccess(null), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingRowId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 rounded-lg" style={CARD_STYLE}>
        <p className="font-mono text-sm" style={{ color: 'var(--a)' }}>Loading pricing…</p>
      </div>
    );
  }

  const selectedCourse = courses.find((c) => c.id === selectedId);

  return (
    <div className="space-y-5" data-testid="admin-pricing-control-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
            section · pricing-control
          </p>
          <h2 className="font-display text-2xl font-bold mt-1" style={{ color: 'var(--white)' }}>Pricing Control</h2>
        </div>
        <div className="flex items-center gap-3">
          {isDirty && (
            <span
              className="font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1 rounded"
              style={{ color: 'var(--warn)', background: 'var(--warn-10)' }}
              data-testid="admin-pricing-dirty"
            >
              unsaved
            </span>
          )}
          <Button
            data-testid="admin-pricing-save-all"
            onClick={handleSaveAll}
            disabled={!isDirty || saving}
            className="btn-shimmer font-body text-sm h-10 px-4 rounded-[3px]"
            style={{
              background: 'var(--a)',
              color: 'var(--white)',
              opacity: !isDirty || saving ? 0.5 : 1,
            }}
          >
            <Save size={14} className="mr-1.5" />
            {saving ? 'Saving…' : 'Save All Changes'}
          </Button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded" style={{ background: 'var(--danger-10)', border: '1px solid var(--danger)' }}>
          <p className="font-body text-xs" style={{ color: 'var(--danger)' }}>{error}</p>
        </div>
      )}
      {success && (
        <div className="p-3 rounded" style={{ background: 'var(--ok-10)', border: '1px solid var(--ok)' }}>
          <p className="font-body text-xs" style={{ color: 'var(--ok)' }}>{success}</p>
        </div>
      )}

      {/* Course selector */}
      <div className="p-5 rounded-lg" style={CARD_STYLE}>
        <label className="font-body text-xs mb-2 block" style={{ color: 'var(--ts)' }}>Course</label>
        <select
          data-testid="admin-pricing-course-select"
          value={selectedId || ''}
          onChange={(e) => handleSelect(e.target.value)}
          className="w-full font-body text-sm h-11 rounded-[3px] px-3"
          style={INPUT_STYLE}
        >
          {courses.map((c) => (
            <option key={c.id} value={c.id} style={{ background: 'var(--l1)' }}>
              {c.name} · {c.track}
            </option>
          ))}
        </select>
      </div>

      {/* Per-course pricing */}
      {selectedCourse && courseDraft && (
        <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid="admin-pricing-course-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>tier pricing</p>
              <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
                {selectedCourse.name}
              </h3>
            </div>
          </div>

          {/* 3 tiers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {['foundation', 'practitioner', 'advanced'].map((lvl) => (
              <div key={lvl}>
                <label className="font-body text-xs mb-1 block capitalize" style={{ color: 'var(--ts)' }}>{lvl}</label>
                <div className="relative">
                  <span
                    className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm"
                    style={{ color: 'var(--te)' }}
                  >£</span>
                  <Input
                    data-testid={`admin-pricing-price-${lvl}`}
                    type="number"
                    min={0}
                    step={1}
                    value={courseDraft.prices[lvl]}
                    onChange={(e) => setCourseDraft((d) => ({
                      ...d,
                      prices: { ...d.prices, [lvl]: Number(e.target.value) },
                    }))}
                    className="font-mono text-sm h-11 rounded-[3px] pl-8"
                    style={INPUT_STYLE}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* Corporate */}
          <div className="mb-5">
            <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Corporate</label>
            <Input
              data-testid="admin-pricing-corporate"
              value={courseDraft.corporate}
              onChange={(e) => setCourseDraft((d) => ({ ...d, corporate: e.target.value }))}
              placeholder="Contact for Quote"
              className="font-body text-sm h-11 rounded-[3px]"
              style={INPUT_STYLE}
            />
          </div>

          {/* Toggles */}
          <div data-testid="admin-pricing-toggles">
            <ToggleRow
              label="Enable Instalment Plans"
              hint="Allow learners to pay in monthly instalments."
              checked={courseDraft.enable_instalments}
              onChange={(v) => setCourseDraft((d) => ({ ...d, enable_instalments: v }))}
              testId="admin-pricing-toggle-instalments"
            />
            <ToggleRow
              label="Founding Intake Badge"
              hint="Display the founding-intake badge on this course."
              checked={courseDraft.founding_intake_badge}
              onChange={(v) => setCourseDraft((d) => ({ ...d, founding_intake_badge: v }))}
              testId="admin-pricing-toggle-badge"
            />
            <ToggleRow
              label="Show Founding Countdown"
              hint="Reveal the deadline countdown on the course page."
              checked={courseDraft.show_founding_countdown}
              onChange={(v) => setCourseDraft((d) => ({ ...d, show_founding_countdown: v }))}
              testId="admin-pricing-toggle-countdown"
            />
          </div>

          {/* Conditional deadline */}
          {courseDraft.show_founding_countdown && (
            <div
              className="mt-4 p-3 rounded"
              style={{ background: 'var(--a-glow)', border: '1px solid var(--a-border)' }}
              data-testid="admin-pricing-deadline-wrap"
            >
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--tm)' }}>
                <CalendarDays size={12} className="inline -mt-0.5 mr-1" />
                Founding Intake Deadline
              </label>
              <Input
                data-testid="admin-pricing-course-deadline"
                type="date"
                value={courseDraft.founding_intake_deadline || ''}
                onChange={(e) => setCourseDraft((d) => ({ ...d, founding_intake_deadline: e.target.value }))}
                className="font-mono text-sm h-11 rounded-[3px]"
                style={INPUT_STYLE}
              />
            </div>
          )}
        </div>
      )}

      {/* Promo codes */}
      <div className="rounded-lg overflow-hidden" style={CARD_STYLE} data-testid="admin-promo-codes">
        <div className="p-6 flex items-center justify-between" style={{ borderBottom: '1px solid var(--b1)' }}>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>active</p>
            <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
              <Tag size={14} className="inline -mt-0.5 mr-1.5" style={{ color: 'var(--a)' }} />
              Promo Codes
            </h3>
          </div>
          <Button
            data-testid="admin-promo-add"
            onClick={() => setPromoModalOpen(true)}
            className="btn-shimmer font-body text-sm h-9 px-3 rounded-[3px]"
            style={{ background: 'var(--a)', color: 'var(--white)' }}
          >
            <Plus size={12} className="mr-1" />
            Add New Code
          </Button>
        </div>

        <table className="w-full">
          <thead>
            <tr style={{ background: 'var(--l2)', borderBottom: '1px solid var(--b1)' }}>
              <Th>Code</Th>
              <Th>Discount</Th>
              <Th align="right">Used</Th>
              <Th>Expires</Th>
              <Th>Status</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {promoCodes.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center font-body text-sm" style={{ color: 'var(--te)' }}>
                  No promo codes yet.
                </td>
              </tr>
            )}
            {promoCodes.map((p, i) => (
              <tr
                key={p.id}
                data-testid={`admin-promo-row-${p.id}`}
                style={{ borderBottom: i === promoCodes.length - 1 ? 'none' : '1px solid var(--b1)' }}
              >
                <Td>
                  <span className="font-mono text-sm tracking-[0.1em]" style={{ color: 'var(--white)' }}>{p.code}</span>
                </Td>
                <Td>
                  <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>
                    {p.discount_type === 'percentage' ? `${p.discount_value}% off` : `£${p.discount_value} off`}
                  </span>
                </Td>
                <Td align="right">
                  <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>
                    Used: {p.uses} / {p.max_uses !== null ? p.max_uses : '∞'}
                  </span>
                </Td>
                <Td>
                  <span className="font-mono text-xs" style={{ color: 'var(--tm)' }}>{p.expires_at}</span>
                </Td>
                <Td><PromoStatusBadge status={p.status} /></Td>
                <Td align="right">
                  <button
                    data-testid={`admin-promo-delete-${p.id}`}
                    onClick={() => handleDeletePromo(p)}
                    disabled={deletingId === p.id}
                    className="flex items-center justify-center rounded ml-auto"
                    style={{
                      width: 28,
                      height: 28,
                      color: 'var(--danger)',
                      border: '1px solid var(--b1)',
                      opacity: deletingId === p.id ? 0.5 : 1,
                    }}
                    title="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Global pricing rules */}
      {rulesDraft && (
        <div className="p-6 rounded-lg" style={CARD_STYLE} data-testid="admin-pricing-rules">
          <div className="mb-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>global</p>
            <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
              Pricing Rules
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Founding Intake Deadline</label>
              <Input
                data-testid="admin-rules-deadline"
                type="date"
                value={rulesDraft.founding_intake_deadline || ''}
                onChange={(e) => setRulesDraft((d) => ({ ...d, founding_intake_deadline: e.target.value }))}
                className="font-mono text-sm h-11 rounded-[3px]"
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Available Slots Per Intake</label>
              <Input
                data-testid="admin-rules-places"
                type="number"
                min={1}
                max={1000}
                value={rulesDraft.available_slots_per_intake}
                onChange={(e) => setRulesDraft((d) => ({ ...d, available_slots_per_intake: Number(e.target.value) }))}
                className="font-mono text-sm h-11 rounded-[3px]"
                style={INPUT_STYLE}
              />
            </div>
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Currency</label>
              <select
                data-testid="admin-rules-currency"
                value={rulesDraft.currency}
                onChange={(e) => setRulesDraft((d) => ({ ...d, currency: e.target.value }))}
                className="w-full font-body text-sm h-11 rounded-[3px] px-3"
                style={INPUT_STYLE}
              >
                {CURRENCIES.map((c) => (
                  <option key={c.value} value={c.value} style={{ background: 'var(--l1)' }}>{c.label}</option>
                ))}
              </select>
              <p className="font-mono text-[10px] mt-1" style={{ color: 'var(--te)' }}>
                current symbol: {CURRENCY_SYMBOL[rulesDraft.currency]}
              </p>
            </div>
            <div>
              <label className="font-body text-xs mb-1 block" style={{ color: 'var(--ts)' }}>Refund Window (days)</label>
              <Input
                data-testid="admin-rules-refund"
                type="number"
                min={0}
                max={365}
                value={rulesDraft.refund_window_days}
                onChange={(e) => setRulesDraft((d) => ({ ...d, refund_window_days: Number(e.target.value) }))}
                className="font-mono text-sm h-11 rounded-[3px]"
                style={INPUT_STYLE}
              />
            </div>
          </div>

          <div className="mt-2">
            <ToggleRow
              label="Show VAT Inclusive"
              hint="Display all prices with VAT included on public pages."
              checked={rulesDraft.show_vat_inclusive}
              onChange={(v) => setRulesDraft((d) => ({ ...d, show_vat_inclusive: v }))}
              testId="admin-rules-toggle-vat"
            />
            <ToggleRow
              label="Enable Corporate Enquiry Flow"
              hint="Show the corporate contact option at checkout."
              checked={rulesDraft.enable_corporate_enquiry_flow}
              onChange={(v) => setRulesDraft((d) => ({ ...d, enable_corporate_enquiry_flow: v }))}
              testId="admin-rules-toggle-corporate"
            />
            <ToggleRow
              label="Full-Pay Discount 5%"
              hint="Apply 5% discount when a learner pays in full up-front."
              checked={rulesDraft.full_pay_discount_5_pct}
              onChange={(v) => setRulesDraft((d) => ({ ...d, full_pay_discount_5_pct: v }))}
              testId="admin-rules-toggle-fullpay"
            />
          </div>
        </div>
      )}

      {/* Service Pricing (Section 5) ─────────────────────────────── */}
      <div className="rounded-lg overflow-hidden" style={CARD_STYLE} data-testid="admin-service-pricing">
        <div className="p-6" style={{ borderBottom: '1px solid var(--b1)' }}>
          <p className="font-mono text-[10px] uppercase tracking-[0.2em]" style={{ color: 'var(--ts)' }}>services</p>
          <h3 className="font-display text-base font-semibold" style={{ color: 'var(--white)' }}>
            Service Pricing
          </h3>
          <p className="font-body text-xs mt-1" style={{ color: 'var(--ts)' }}>
            These rows feed the /pricing questionnaires and the /reach packages. Leave price blank to show
            "Price on consultation" on public pages.
          </p>
        </div>
        <table className="w-full">
          <thead>
            <tr style={{ background: 'var(--l2)', borderBottom: '1px solid var(--b1)' }}>
              <Th>Service</Th>
              <Th align="right">Starting From</Th>
              <Th>Per Month</Th>
              <Th>Notes</Th>
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {servicePricingMeta.current.map((row, idx) => {
              const id = row.id;
              const draft = servicePricingDrafts[id] || { price: '', per_month: false, notes: '' };
              const orig  = servicePricingOriginal[id] || draft;
              const dirty = JSON.stringify(draft) !== JSON.stringify(orig);
              return (
                <tr
                  key={id}
                  data-testid={`admin-service-pricing-row-${id}`}
                  style={{ borderBottom: idx === servicePricingMeta.current.length - 1 ? 'none' : '1px solid var(--b1)' }}
                >
                  <Td>
                    <p className="font-body text-sm" style={{ color: 'var(--white)' }}>{row.label}</p>
                    <p className="font-mono text-[10px] mt-0.5 uppercase tracking-[0.15em]" style={{ color: 'var(--ts)' }}>
                      Acodia {row.service}
                    </p>
                  </Td>
                  <Td align="right">
                    <div className="relative" style={{ width: 140, marginLeft: 'auto' }}>
                      <span
                        className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-sm"
                        style={{ color: 'var(--te)' }}
                      >£</span>
                      <Input
                        data-testid={`admin-service-pricing-price-${id}`}
                        type="number"
                        min={0}
                        value={draft.price}
                        onChange={(e) => updateServicePricingDraft(id, { price: e.target.value })}
                        placeholder="—"
                        className="font-mono text-sm h-10 rounded-[3px] pl-7"
                        style={INPUT_STYLE}
                      />
                    </div>
                  </Td>
                  <Td>
                    <Toggle
                      checked={draft.per_month}
                      onChange={(v) => updateServicePricingDraft(id, { per_month: v })}
                      testId={`admin-service-pricing-permonth-${id}`}
                    />
                  </Td>
                  <Td>
                    <Input
                      data-testid={`admin-service-pricing-notes-${id}`}
                      value={draft.notes}
                      onChange={(e) => updateServicePricingDraft(id, { notes: e.target.value })}
                      placeholder="e.g. Price on consultation"
                      className="font-body text-sm h-10 rounded-[3px]"
                      style={INPUT_STYLE}
                      maxLength={200}
                    />
                  </Td>
                  <Td align="right">
                    <Button
                      data-testid={`admin-service-pricing-save-${id}`}
                      onClick={() => handleSaveServicePricingRow(id)}
                      disabled={!dirty || savingRowId === id}
                      className="font-body text-xs h-9 px-3 rounded-[3px]"
                      style={{
                        background: dirty ? 'var(--a)' : 'transparent',
                        color: dirty ? 'var(--white)' : 'var(--ts)',
                        border: dirty ? 'none' : '1px solid var(--b1)',
                        opacity: !dirty || savingRowId === id ? 0.6 : 1,
                      }}
                    >
                      {savingRowId === id ? 'Saving…' : dirty ? 'Save' : 'Saved'}
                    </Button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {promoModalOpen && (
        <PromoModal
          onCancel={() => setPromoModalOpen(false)}
          onSave={handleCreatePromo}
          saving={creatingPromo}
        />
      )}
    </div>
  );
}

// ─── Table helpers ────────────────────────────────────────────────
function Th({ children, align = 'left' }) {
  return (
    <th
      className="font-mono text-[10px] uppercase tracking-[0.15em] px-4 py-3"
      style={{ color: 'var(--ts)', textAlign: align }}
    >
      {children}
    </th>
  );
}
function Td({ children, align = 'left' }) {
  return <td className="px-4 py-3" style={{ textAlign: align, verticalAlign: 'middle' }}>{children}</td>;
}
